import {
  useGetBasicEnergyAccountQuery,
  usePostBasicEnergyAccountMutation,
  useUpdateBasicEnergySubscriptionMutation,
} from '@/api/energy-analysis/basicEnergySlice';
import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Switch,
  Typography,
} from '@mui/material';

const ManageBasicEnergySubscription = ({ account }) => {
  const defaultSubscription = {
    account_id: account.AccountId,
    water: false,
    heat: false,
    power: false,
  };

  const {
    data: subscription,
    isLoading: subscriptionIsLoading,
    error: subscriptionError,
  } = useGetBasicEnergyAccountQuery(
    { account_id: account.AccountId },
    { skip: !account?.AccountId },
  );

  const [createSubscription, { isLoading: isCreateLoading }] =
    usePostBasicEnergyAccountMutation();
  const [updateSubscription, { isLoading: isUpdateLoading }] =
    useUpdateBasicEnergySubscriptionMutation();

  const hasPowerBasicService = account.Services?.some((s) => s.Id === 7);
  const hasHeatBasicService = account.Services?.some((s) => s.Id === 8);
  const hasWaterBasicService = account.Services?.some((s) => s.Id === 9);
  const is404 = subscriptionError?.status === 404;
  const is500 = subscriptionError?.status === 500;

  const renderErrorOrMissingState = () => {
    if (subscriptionIsLoading) return <CircularProgress />;
    if (is500)
      return <Alert severity="error">Der skete en uventet fejl.</Alert>;
    if (is404) {
      return hasPowerBasicService ||
        hasHeatBasicService ||
        hasWaterBasicService ? (
        <Alert
          action={
            <Button
              onClick={() =>
                createSubscription({
                  AccountId: defaultSubscription.account_id,
                  Power: false,
                  Heat: false,
                  Water: false,
                })
              }
            >
              Opret
            </Button>
          }
          severity="warning"
        >
          Der er ikke oprettet en Basic Energy subscription på denne konto.
        </Alert>
      ) : (
        <Button
          variant="contained"
          onClick={() =>
            createSubscription({
              AccountId: defaultSubscription.account_id,
              Power: false,
              Heat: false,
              Water: false,
            })
          }
        >
          Opret
        </Button>
      );
    }
    return null;
  };

  const renderSubscriptionControls = () => {
    if (!subscription) return null;

    return (
      <>
        {hasPowerBasicService && !subscription.Power && (
          <Alert severity="warning">
            Power Basic Service er slået til men Power analysen er ikke
            oprettet.
          </Alert>
        )}
        {hasHeatBasicService && !subscription.Heat && (
          <Alert severity="warning">
            Heat Basic Service er slået til men Heat analysen er ikke oprettet.
          </Alert>
        )}
        {hasWaterBasicService && !subscription.Water && (
          <Alert severity="warning">
            Water Basic Service er slået til men Water analysen er ikke
            oprettet.
          </Alert>
        )}
        {!hasPowerBasicService && subscription.Power && (
          <Alert severity="warning">
            Power analyse er oprettet men service er ikke slået til.
          </Alert>
        )}
        {!hasHeatBasicService && subscription.Heat && (
          <Alert severity="warning">
            Heat analyse er oprettet men service er ikke slået til.
          </Alert>
        )}
        {!hasWaterBasicService && subscription.Water && (
          <Alert severity="warning">
            Water analyse er oprettet men service er ikke slået til. oprettet.
          </Alert>
        )}
        {['Power', 'Heat', 'Water'].map((type, index) => {
          return (
            <Box key={type}>
              {index > 0 && <Divider sx={{ mx: 1 }} />}
              <Box display="flex" alignItems="center" sx={{ pl: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {type}
                </Typography>
                <Switch
                  checked={subscription[type]}
                  onChange={() =>
                    updateSubscription({
                      account_id: subscription.AccountId,
                      body: {
                        AccountId: subscription.AccountId,
                        Power:
                          type === 'Power'
                            ? !subscription.Power
                            : subscription.Power,
                        Heat:
                          type === 'Heat'
                            ? !subscription.Heat
                            : subscription.Heat,
                        Water:
                          type === 'Water'
                            ? !subscription.Water
                            : subscription.Water,
                      },
                    })
                  }
                  inputProps={{ 'aria-label': `toggle ${type}` }}
                />
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <>
      <HeaderDisplay
        label={'Basic Energy subscription'}
        isLoading={isCreateLoading || isUpdateLoading}
      />
      {renderErrorOrMissingState()}
      {renderSubscriptionControls()}
    </>
  );
};

export default ManageBasicEnergySubscription;
const HeaderDisplay = ({ label, description, isLoading }) => {
  return (
    <Box display={'flex'} justifyContent={'space-between'}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          mb: 2,
        }}
      >
        <Typography variant="body1" color="textSecondary" fontWeight="bold">
          {label}
        </Typography>
        {description && <InfoTooltipIcon title={description} />}
      </Box>
      {isLoading && <CircularProgress size={16} />}
    </Box>
  );
};
